import type { ChainStoreReportsFavorite } from '../domain/chain/types';
import type { DataChainStoreShuGraphParams } from '../domain/dataChainStoreShuGraph';
import type { DataKiGraphParams } from '../domain/dataKiGraph';
import type { HallReportsFavorite } from '../domain/hall/types';
import type { ModelReportsFavorite } from '../domain/ki/types';

import {
  fieldCodeHasRestrictedKyoyuGroupPrefix,
  isKyoyuGroupWithHiddenSyokenJitenFields,
  stripRestrictedKyoyuGroupFieldCodes,
} from './restrictedFieldsByKyoyuGroup';

/**
 * 制限共有グループ向けのお気に入りサニタイズ（機種集計・店舗レポート・チェーン店レポート）。
 * 機種別商圏は処理・ドキュメントがまとまるよう `sanitizeKiSyokenFavoriteForRestrictedKyoyu.ts` に分離し、末尾から再 export する。
 */

type ParamsWithFields = { fields?: string[] };

function sanitizeParamsWithFields<T extends ParamsWithFields>(params: T): T {
  return {
    ...params,
    fields: stripRestrictedKyoyuGroupFieldCodes(params.fields),
  };
}

/** 種別（または行キー）ごとの params から `fields` 接頭辞だけ除去する */
function sanitizeKeyedParamsRecord<T extends Record<string, ParamsWithFields>>(
  record: T
): T {
  return Object.fromEntries(
    Object.entries(record).map(([key, params]) => [
      key,
      sanitizeParamsWithFields(params),
    ])
  ) as T;
}

/** 種別ごとの列コード配列から、接頭辞付きコードだけ除去する */
function stripKeyedColumnOrderRecord<
  T extends Record<string, string[] | undefined>
>(record: T): T {
  return Object.fromEntries(
    Object.entries(record).map(([key, codes]) => [
      key,
      stripRestrictedKyoyuGroupFieldCodes(codes),
    ])
  ) as T;
}

/**
 * 店舗レポート `dataHallKi` / チェーン店 `dataChainStoreKi` のように、
 * `setting` と `columnsOrder` が種別キー配下にあるブロックを一括サニタイズする。
 */
function sanitizeShuKeyedKiFavoriteBlock<
  T extends {
    setting: Record<string, ParamsWithFields>;
    columnsOrder: Record<string, string[] | undefined>;
  }
>(block: T): T {
  return {
    ...block,
    setting: sanitizeKeyedParamsRecord(block.setting),
    columnsOrder: stripKeyedColumnOrderRecord(block.columnsOrder),
  };
}

/**
 * 推移グラフの表示項目から自店・商圏接頭辞のものを除去する。
 * 複数スロット化以前の単一 `field` と、複数グラフスロット `graphSlots[].field` の両方を対象にする。
 */
export function sanitizeDataKiGraphSetting(
  setting: DataKiGraphParams
): DataKiGraphParams {
  let result = setting;

  // 複数スロット化以前のお気に入りが持つ単一 `field`
  if (
    result.field != null &&
    fieldCodeHasRestrictedKyoyuGroupPrefix(String(result.field))
  ) {
    const { field: _discardedGraphField, ...restSetting } = result;
    result = restSetting;
  }

  // 複数スロット: `field` が制限接頭辞のスロットを（1スロット=1グラフなので）配列ごと除去する
  if (result.graphSlots != null) {
    const filteredSlots = result.graphSlots.filter(
      (slot) =>
        slot.field === '' ||
        !fieldCodeHasRestrictedKyoyuGroupPrefix(String(slot.field))
    );
    if (filteredSlots.length !== result.graphSlots.length) {
      if (filteredSlots.length > 0) {
        result = { ...result, graphSlots: filteredSlots };
      } else {
        // 全スロットが除去対象 → キーごと落として resolveDataKiGraphSlots のデフォルト（out）に委ねる
        const { graphSlots: _discardedSlots, ...restSetting } = result;
        result = restSetting;
      }
    }
  }

  return result;
}

function sanitizeChainStoreShuGraphParams(
  graphParams: DataChainStoreShuGraphParams
): DataChainStoreShuGraphParams {
  return {
    ...graphParams,
    fieldsForLine: stripRestrictedKyoyuGroupFieldCodes(
      graphParams.fieldsForLine
    ),
    fieldsForBar: stripRestrictedKyoyuGroupFieldCodes(graphParams.fieldsForBar),
  };
}

function sanitizeModelReportsDataKi3rdRow(
  rows: ModelReportsFavorite['dataKi3rdRow']
): ModelReportsFavorite['dataKi3rdRow'] {
  if (rows == null) {
    return undefined;
  }
  const headRow = rows.at(0);
  if (headRow == null) {
    return undefined;
  }
  return [{ ...headRow, setting: sanitizeParamsWithFields(headRow.setting) }];
}

function sanitizeModelReportsDataKi2ndRow(
  dataKi2ndRow: NonNullable<ModelReportsFavorite['dataKi2ndRow']>
): NonNullable<ModelReportsFavorite['dataKi2ndRow']> {
  return {
    ...dataKi2ndRow,
    setting: Object.fromEntries(
      Object.entries(dataKi2ndRow.setting).map(([rowKey, rowSetting]) => [
        rowKey,
        {
          ...rowSetting,
          params: sanitizeParamsWithFields(rowSetting.params),
        },
      ])
    ),
  };
}

/**
 * 制限共有グループのユーザー向けに、機種集計お気に入りのうち
 * メインテーブル・展開行・推移グラフなど（`jiten` / `syoken` が混入し得る箇所）だけをサニタイズする。
 * 制限対象外のユーザーでは入力をそのまま返す。
 */
export function sanitizeModelReportsFavoriteForRestrictedKyoyu(
  favorite: ModelReportsFavorite,
  kyoyuGroupCode: string | undefined
): ModelReportsFavorite {
  if (!isKyoyuGroupWithHiddenSyokenJitenFields(kyoyuGroupCode)) {
    return favorite;
  }

  return {
    ...favorite,
    dataKi: favorite.dataKi
      ? {
          ...favorite.dataKi,
          setting: sanitizeParamsWithFields(favorite.dataKi.setting),
          columnsOrder: stripRestrictedKyoyuGroupFieldCodes(
            favorite.dataKi.columnsOrder
          ),
        }
      : undefined,
    dataKi2ndRow:
      favorite.dataKi2ndRow == null
        ? undefined
        : sanitizeModelReportsDataKi2ndRow(favorite.dataKi2ndRow),
    dataKi3rdRow: sanitizeModelReportsDataKi3rdRow(favorite.dataKi3rdRow),
    dataKiGraph: favorite.dataKiGraph
      ? {
          ...favorite.dataKiGraph,
          setting: sanitizeDataKiGraphSetting(favorite.dataKiGraph.setting),
        }
      : undefined,
  };
}

/**
 * 制限共有グループのユーザー向けに、店舗レポートお気に入りから自店・商圏接頭辞の項目を取り除く。
 */
export function sanitizeHallReportsFavoriteForRestrictedKyoyu(
  favorite: HallReportsFavorite,
  kyoyuGroupCode: string | undefined
): HallReportsFavorite {
  if (!isKyoyuGroupWithHiddenSyokenJitenFields(kyoyuGroupCode)) {
    return favorite;
  }

  const dataHallKi = favorite.dataHallKi;

  return {
    ...favorite,
    dataHallShu: favorite.dataHallShu
      ? {
          ...favorite.dataHallShu,
          setting: sanitizeParamsWithFields(favorite.dataHallShu.setting),
          columnsOrder: stripRestrictedKyoyuGroupFieldCodes(
            favorite.dataHallShu.columnsOrder
          ),
        }
      : undefined,
    dataHallKi:
      dataHallKi == null
        ? undefined
        : sanitizeShuKeyedKiFavoriteBlock(dataHallKi),
  };
}

/**
 * 制限共有グループのユーザー向けに、チェーン店レポートお気に入りから自店・商圏接頭辞の項目を取り除く。
 */
export function sanitizeChainStoreReportsFavoriteForRestrictedKyoyu(
  favorite: ChainStoreReportsFavorite,
  kyoyuGroupCode: string | undefined
): ChainStoreReportsFavorite {
  if (!isKyoyuGroupWithHiddenSyokenJitenFields(kyoyuGroupCode)) {
    return favorite;
  }

  const dataChainStoreShu = favorite.dataChainStoreShu;
  const dataChainStoreShuGraph = favorite.dataChainStoreShuGraph;
  const dataChainStoreKi = favorite.dataChainStoreKi;

  return {
    ...favorite,
    dataChainStoreShu:
      dataChainStoreShu == null
        ? undefined
        : {
            ...dataChainStoreShu,
            setting: sanitizeKeyedParamsRecord(dataChainStoreShu.setting),
            columnsOrder: stripKeyedColumnOrderRecord(
              dataChainStoreShu.columnsOrder
            ),
          },
    dataChainStoreShuWhole: favorite.dataChainStoreShuWhole
      ? {
          ...favorite.dataChainStoreShuWhole,
          setting: sanitizeParamsWithFields(
            favorite.dataChainStoreShuWhole.setting
          ),
          columnsOrder: stripRestrictedKyoyuGroupFieldCodes(
            favorite.dataChainStoreShuWhole.columnsOrder
          ),
        }
      : undefined,
    dataChainStoreShuGraph:
      dataChainStoreShuGraph == null
        ? undefined
        : {
            ...dataChainStoreShuGraph,
            setting: Object.fromEntries(
              Object.entries(dataChainStoreShuGraph.setting).map(
                ([graphKey, graphParams]) => [
                  graphKey,
                  sanitizeChainStoreShuGraphParams(graphParams),
                ]
              )
            ),
          },
    dataChainStoreKi:
      dataChainStoreKi == null
        ? undefined
        : sanitizeShuKeyedKiFavoriteBlock(dataChainStoreKi),
  };
}

export { sanitizeKiSyokenFavoriteForRestrictedKyoyu } from './sanitizeKiSyokenFavoriteForRestrictedKyoyu';
